import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import Auth from '../../api/auth';
import GitlabWikiHelpButton from '../../components/GitlabWikiHelpButton';
import Table from '../../components/Table';
import useApiQuery from '../../hooks/useApiQuery';
import useAppContext from '../../hooks/useAppContext';
import rbacCheck from '../../rbac/rbacCheck';

import CreateOrganizationFormModal from './CreateOrganizationFormModal';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg, ErrorType } from '@/types';
import type { Organization } from '@/types/organization';
import type { TableRowData } from '@/types/tableTypes';

const organizationsGuideSlug = 'user-guide/organizations';

const Organizations: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const userData = useUserData();
  const contextState = useAppContext();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);

  const { error: fetchError, makeApiCall: getOrganizationsCall } = useApiQuery(
    Auth.fetchV1Organizations as unknown as APIHookArg
  );

  const canCreateOrganization = rbacCheck(userData.roles, '/organization:POST');

  useEffect(() => {
    const getOrganizations = async (): Promise<void> => {
      try {
        const result = (await getOrganizationsCall()) as Organization[];
        setOrganizations(result);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    getOrganizations();
    contextState.setSelectedOrganization?.(null);
  }, [contextState, getOrganizationsCall]);

  const fields = [
    {
      title: 'Name',
      id: 'enterpriseName',
    },
    {
      title: 'CRMid',
      id: 'crmid',
    },
    {
      title: 'Account Owner',
      id: 'accountowner',
    },
  ];

  const rows = organizations.map(org => ({
    key: org._id ?? '',
    onRowClick: () => {
      contextState.setSelectedOrganization?.(org);
      navigate(`/governance/organizations/${org._id ?? ''}`);
    },
    data: [
      {
        field: 'enterpriseName',
        value: org.enterpriseName,
      },
      {
        field: 'crmid',
        value: org.CRMId,
      },
      {
        field: 'accountowner',
        value: org.accountOwner?.email,
      },
    ],
  })) as TableRowData[];

  return (
    <>
      {createModalOpened && (
        <CreateOrganizationFormModal
          onSubmit={(newOrganization: Organization) => {
            setOrganizations(prev => [...prev, newOrganization]);
            setCreateModalOpened(false);
          }}
          onCancel={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      <Table
        header='Organizations'
        fields={fields}
        rows={rows}
        error={fetchError as ErrorType}
        headerActionSlot={{
          content: (
            <GitlabWikiHelpButton
              slug={organizationsGuideSlug}
              tooltipMessageId='organizationsGuide'
              tooltipDefaultMessage='Organizations guide'
              modalTitleMessageId='organizationsGuide'
              modalTitleDefaultMessage='Organizations guide'
              buttonAriaLabel='Open organizations guide'
              buttonSx={{ mt: 0 }}
            />
          ),
        }}
        createAction={{
          disabled: !canCreateOrganization,
          onClick: () => {
            setCreateModalOpened(true);
          },
          tooltip: !canCreateOrganization
            ? intl.formatMessage({ id: 'organizationCreationPermissionErr' })
            : '',
        }}
      />
    </>
  );
};

export default Organizations;
